<script setup>
/**
 * 上映スケジュールAI ステップ4：タイムチャートの出力・共有（DEV-14984）
 *
 * - 対象期間（開始日〜終了日、最大 14 日。保存済みの期間から選ぶこともできる）を選び、保存済みタイムチャートのプレビューを表示
 * - 未満足のハード制約・物理的な違反があれば上部に赤い横帯で一覧表示
 * - CSV / Excel / PDF をダウンロード（dp:scheduleAi:plan:export 権限が必要）
 * 時刻は営業日基準の文字列（25:30 など）をバックエンドの値のまま表示し、変換しない。
 * 出力はタイムチャートを変更しないため、ダウンロード後にステップ3 を更新する必要はない。
 */
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { saveAs } from "file-saver";
import {
  getScheduleAiExportPreviewService,
  downloadScheduleAiExportService,
  getScheduleAiPlanPeriodsService,
} from "@/api/dp/index.js";
import {
  defaultPeriod,
  isValidPeriod,
  periodFromQuery,
  shortDateLabel,
  prereqStepOf,
} from "../plan/utils";
import PeriodPicker from "../plan/components/period-picker.vue";

// 前提ステップ・ステップ3 のメニュー path（動的ルートのため実際の path は登録済みルートから探す）
const SETTING_ROUTE_PATH = "scheduleAiSetting";
const MOVIE_ROUTE_PATH = "scheduleAiMovie";
const PLAN_ROUTE_PATH = "scheduleAiPlan";
// 出力形式（format はバックエンドに送る値）
const FORMATS = [
  { format: "csv", label: "CSV", icon: "Document" },
  { format: "xlsx", label: "Excel", icon: "Grid" },
  { format: "pdf", label: "PDF", icon: "Printer" },
];
// ダウンロード失敗のコード
const CODE_CONFLICT = 409; // 版が変わった（ステップ3 で再保存された）
const CODE_EMPTY = 4603; // タイムチャートが空

const route = useRoute();
const router = useRouter();

// 対象期間（yyyy-MM-dd、両端を含む）
const dateFrom = ref("");
const dateTo = ref("");
// 保存済みの期間一覧（期間選択のドロップダウン用）
const periods = ref([]);
const loading = ref(false);
const preview = ref(null);
// 取得失敗時のメッセージ。値があれば本文を出さない
const loadErrorMsg = ref("");
// 取得失敗の原因になった前提ステップ（"setting" | "movie" | null）
const loadErrorStep = ref(null);
// ダウンロード中の形式（同時に 1 つだけ）
const downloadingFormat = ref("");

// ==================== 前提ステップへのリンク ====================

/**
 * メニュー path の末尾一致で登録済みルートを探す（権限がなければ null）
 */
const findRoutePath = (path) => router.getRoutes().find((r) => r.path.endsWith(`/${path}`))?.path || null;
const settingPath = computed(() => findRoutePath(SETTING_ROUTE_PATH));
const moviePath = computed(() => findRoutePath(MOVIE_ROUTE_PATH));
const planPath = computed(() => findRoutePath(PLAN_ROUTE_PATH));

/**
 * ステップ3 を同じ期間で開く
 */
const goPlan = () => {
  if (planPath.value) router.push({ path: planPath.value, query: { dateFrom: dateFrom.value, dateTo: dateTo.value } });
};

// ==================== 取得 ====================

// 取得の世代（期間を素早く切り替えたとき古い応答で上書きしないため）
let loadSeq = 0;

/**
 * プレビューを取得。silent=true のときは画面を残したまま再取得する（ダウンロード時の版ずれ）
 */
const loadPreview = async ({ silent = false } = {}) => {
  if (!dateFrom.value || !dateTo.value) return;
  loadSeq += 1;
  const seq = loadSeq;
  if (!silent) {
    loading.value = true;
    loadErrorMsg.value = "";
    loadErrorStep.value = null;
  }
  try {
    const res = await getScheduleAiExportPreviewService({ dateFrom: dateFrom.value, dateTo: dateTo.value });
    if (seq !== loadSeq) return;
    preview.value = res?.data || null;
    loadErrorMsg.value = "";
    loadErrorStep.value = null;
  } catch (e) {
    if (seq !== loadSeq) return;
    // 再取得の失敗は画面を残す（メッセージは request.js でトースト済み）
    if (!silent) {
      preview.value = null;
      loadErrorMsg.value = e?.message || "データの取得に失敗しました";
      loadErrorStep.value = prereqStepOf(loadErrorMsg.value, e?.code);
    }
  } finally {
    if (seq === loadSeq) loading.value = false;
  }
};

// ==================== 期間の選択 ====================

/**
 * 保存済みの期間一覧を取得（失敗しても期間は手で選べるので空のままにする）
 */
const fetchPeriods = async () => {
  try {
    const res = await getScheduleAiPlanPeriodsService();
    periods.value = Array.isArray(res?.data) ? res.data : [];
  } catch {
    // エラーメッセージは request.js でトースト済み
  }
};

const switchPeriod = async ({ dateFrom: from, dateTo: to }) => {
  if (!isValidPeriod(from, to) || (from === dateFrom.value && to === dateTo.value)) return;
  // 再読み込みで同じ期間を開けるよう URL にも残す（旧形式の weekStart は消す）
  const query = { ...route.query, dateFrom: from, dateTo: to };
  delete query.weekStart;
  router.replace({ query });
  dateFrom.value = from;
  dateTo.value = to;
  await loadPreview();
};

// ==================== 表示 ====================

const isEmpty = computed(() => !preview.value || !!preview.value.empty);
const nonCompliant = computed(() => preview.value?.compliance === "NON_COMPLIANT");
const issues = computed(() => preview.value?.issues || []);
const screens = computed(() => preview.value?.screens || []);
// 対象期間（バックエンドの dateFrom〜dateTo をそのまま表示）
const periodText = computed(() => {
  if (!preview.value) return "";
  const { dateFrom: from, dateTo: to } = preview.value;
  return `${from || ""}〜${to || ""}`;
});

/**
 * 日付見出し。例：「10/23(金)」（曜日はバックエンドの値を使う）
 */
const dayHeadLabel = (day) => {
  const label = shortDateLabel(day.bizDate);
  return day.weekday ? label.replace(/\(.+\)$/, `(${day.weekday})`) : label;
};

/**
 * 土日の見出しの色分け用クラス
 */
const weekdayClass = (weekday) => {
  if (weekday === "土") return "is-sat";
  if (weekday === "日") return "is-sun";
  return "";
};

// ==================== ダウンロード ====================

/**
 * ダウンロードできない理由（空文字ならダウンロード可）
 */
const downloadBlockReason = computed(() => {
  if (!preview.value) return "プレビューを取得できていません";
  if (preview.value.empty) return preview.value.emptyMessage || "この期間のタイムチャートはまだ保存されていません";
  return "";
});

// ファイル名に使えない文字・空白（全角スペース含む）。JS と Java で \s の範囲が違うため明示した文字集合を使う
const FILENAME_UNSAFE_RE = /[\\/:*?"<>|\t\r\n \u3000]+/g;

/**
 * 保存するファイル名（バックエンド DpScheduleAiExportService.fileName と同じ規則）
 * 例：「TOHOシネマズ 新宿」→ タイムチャート_TOHOシネマズ_新宿_20261023-20261029_v12.pdf
 * 劇場名が空のときは「劇場名_」の部分を付けない
 */
const buildFilename = (format) => {
  const p = preview.value || {};
  const compact = (date) => (date || "").replace(/-/g, "");
  const cinema = (p.cinemaName ?? "").trim().replace(FILENAME_UNSAFE_RE, "_");
  return `タイムチャート_${cinema ? `${cinema}_` : ""}${compact(p.dateFrom)}-${compact(p.dateTo)}_v${p.version}.${format}`;
};

/**
 * 失敗時に Blob で返った AjaxResult を読む（JSON でなければ null）
 */
const readErrorBlob = async (blob) => {
  if (!(blob instanceof Blob) || !(blob.type || "").includes("json")) return null;
  try {
    return JSON.parse(await blob.text());
  } catch {
    return { code: null, msg: "" };
  }
};

const handleDownload = async (format) => {
  if (downloadBlockReason.value || downloadingFormat.value) return;
  const { version } = preview.value;
  const filename = buildFilename(format);
  downloadingFormat.value = format;
  try {
    const blob = await downloadScheduleAiExportService({
      dateFrom: dateFrom.value,
      dateTo: dateTo.value,
      version,
      format,
    });
    const error = await readErrorBlob(blob);
    if (!error) {
      saveAs(blob, filename);
      return;
    }
    ElMessage.warning(error.msg || "ファイルの出力に失敗しました");
    // 版が変わった／空になった場合は最新の状態を取り直す（ボタンの状態・版を更新）
    if (error.code === CODE_CONFLICT || error.code === CODE_EMPTY) await loadPreview({ silent: true });
  } catch {
    // 通信エラーのメッセージは request.js でトースト済み
  } finally {
    downloadingFormat.value = "";
  }
};

// ==================== 初期化 ====================

onMounted(async () => {
  // 期間の指定がなければ、直近の金曜日を含む保存済み期間（なければ金曜日から 1 週間）を開く
  await fetchPeriods();
  const period = periodFromQuery(route.query) || defaultPeriod(periods.value);
  dateFrom.value = period.dateFrom;
  dateTo.value = period.dateTo;
  await loadPreview();
});

const reload = () => loadPreview();
</script>

<template>
  <div v-loading="loading" class="export-page">
    <header class="page-bar">
      <div class="page-bar__title">
        <h2>タイムチャートの出力・共有</h2>
      </div>
      <PeriodPicker :date-from="dateFrom" :date-to="dateTo" :periods="periods" @change="switchPeriod" />
    </header>

    <!-- 取得失敗（ステップ1・2 未登録、権限不足など）。前提ステップが原因のときだけ該当ページへのリンクを出す -->
    <el-result v-if="loadErrorMsg && !loading" icon="warning" :title="loadErrorMsg">
      <template #extra>
        <div class="prereq-links">
          <el-button v-if="settingPath && loadErrorStep === 'setting'" @click="router.push(settingPath)">
            劇場・スクリーン設定へ
          </el-button>
          <el-button v-if="moviePath && loadErrorStep === 'movie'" @click="router.push(moviePath)">
            本館上映作品・バージョン登録へ
          </el-button>
          <el-button type="primary" @click="reload">再読み込み</el-button>
        </div>
      </template>
    </el-result>

    <template v-else-if="preview && !loading">
      <!-- ハード制約の未達・物理的な違反 -->
      <section v-if="nonCompliant" class="issue-banner" role="alert">
        <div class="issue-banner__title">⚠ 未満たしのハード制約があります</div>
        <ul v-if="issues.length" class="issue-list">
          <li v-for="(issue, i) in issues" :key="i" class="issue-item">
            <span class="issue-item__tag" :class="`is-${(issue.category || '').toLowerCase()}`">
              {{ issue.category === "VIOLATION" ? "違反" : "制約未達" }}
            </span>
            <span class="issue-item__title">{{ issue.title }}</span>
            <span v-if="issue.detail" class="issue-item__detail">{{ issue.detail }}</span>
            <span v-if="issue.gap !== null && issue.gap !== undefined" class="issue-item__gap">不足 {{ issue.gap }} 回</span>
          </li>
        </ul>
      </section>

      <!-- 表頭情報・ダウンロード -->
      <section class="panel head-panel">
        <dl class="meta-list">
          <div class="meta-item">
            <dt>劇場名</dt>
            <dd>{{ preview.cinemaName || "—" }}</dd>
          </div>
          <div class="meta-item">
            <dt>対象期間</dt>
            <dd class="num">{{ periodText }}</dd>
          </div>
          <div class="meta-item">
            <dt>版</dt>
            <dd class="num">{{ preview.version !== null && preview.version !== undefined ? `v${preview.version}` : "—" }}</dd>
          </div>
          <div class="meta-item">
            <dt>更新日時</dt>
            <dd class="num">{{ preview.updateTime || "—" }}</dd>
          </div>
          <div class="meta-item">
            <dt>更新者</dt>
            <dd>{{ preview.updateBy || "—" }}</dd>
          </div>
          <div v-if="!isEmpty" class="meta-item">
            <dt>上映回</dt>
            <dd class="num">{{ preview.showCount ?? 0 }}</dd>
          </div>
        </dl>
        <div v-hasPermi="['dp:scheduleAi:plan:export']" class="download-box">
          <div class="download-actions">
            <el-button
              v-for="f in FORMATS"
              :key="f.format"
              type="primary"
              :plain="f.format !== 'xlsx'"
              :icon="f.icon"
              :loading="downloadingFormat === f.format"
              :disabled="!!downloadBlockReason || (!!downloadingFormat && downloadingFormat !== f.format)"
              @click="handleDownload(f.format)"
            >
              {{ f.label }}
            </el-button>
          </div>
          <span v-if="downloadBlockReason" class="download-note">{{ downloadBlockReason }}</span>
        </div>
      </section>

      <!-- タイムチャート未保存 -->
      <section v-if="isEmpty" class="panel">
        <el-empty :image-size="72" :description="preview.emptyMessage || 'この期間のタイムチャートはまだ保存されていません'">
          <el-button v-if="planPath" @click="goPlan">タイムチャート作成へ</el-button>
        </el-empty>
      </section>

      <!-- 可読時刻表（スクリーンごと × 期間の日数） -->
      <template v-else>
        <section v-for="screen in screens" :key="screen.screenId" class="panel screen-panel">
          <div class="panel-head">
            <h3>{{ screen.name }}</h3>
          </div>
          <div class="day-grid-scroll">
            <!-- 対象期間外の日（不正データ）が末尾に追加されることがあるため、列数は days の件数に合わせる -->
            <div class="day-grid" :style="{ '--day-count': screen.days?.length || 1 }">
              <div v-for="day in screen.days" :key="day.bizDate" class="day-col">
                <div class="day-col__head" :class="weekdayClass(day.weekday)">{{ dayHeadLabel(day) }}</div>
                <div v-if="!day.shows?.length" class="day-col__empty">—</div>
                <div
                  v-for="show in day.shows"
                  :key="show.showId"
                  class="show-card"
                  :class="{ 'is-flagged': show.flagged }"
                  :title="show.flagged ? '物理的な違反（重複・清掃間隔・営業時間外など）があります' : ''"
                >
                  <div class="show-card__time num">
                    {{ show.startTime }}<span class="muted">〜{{ show.endTime }}</span>
                  </div>
                  <div class="show-card__title">{{ show.movieTitle }}</div>
                  <div class="show-card__meta">
                    <span>{{ show.versionLabel }}</span>
                  </div>
                  <div class="show-card__meta muted num">
                    本編 {{ show.featureStartTime }}・{{ show.featureMinute }}分
                    <template v-if="show.estSeats !== null && show.estSeats !== undefined">／予測 {{ show.estSeats }}</template>
                    <span v-if="show.sourceLabel" class="show-card__source">{{ show.sourceLabel }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <p v-if="!screens.length" class="muted">スクリーンがありません</p>
      </template>
    </template>
  </div>
</template>

<style lang="less" scoped>
/* ステップ1〜3 と同じデザイントークン */
.export-page {
  --sp-brand: var(--brand-color, #cc251b);
  --sp-panel: var(--bg-panel, #fff);
  --sp-border: var(--border-color, #e5e5e5);
  --sp-text: var(--text-primary, #000);
  --sp-text-2: var(--text-secondary, #4a4a4a);
  --sp-muted: var(--text-tertiary, #757575);
  --sp-tint: color-mix(in srgb, var(--sp-brand) 7%, var(--sp-panel));
  --sp-radius: var(--border-radius, 6px);

  min-height: 200px;
  padding: 0 24px 40px 15px;
  box-sizing: border-box;
  color: var(--sp-text);
}

.muted {
  color: var(--sp-muted);
}

.num {
  font-variant-numeric: tabular-nums;
}

.panel {
  margin-bottom: 16px;
  border: 1px solid var(--sp-border);
  border-radius: var(--sp-radius);
  background: var(--sp-panel);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.panel-head {
  padding: 10px 18px;
  border-bottom: 1px solid var(--sp-border);
  background: var(--sp-tint);

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
  }
}

/* ---------- 操作バー ---------- */
.page-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  padding: 15px 0 12px;

  &__title h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
  }
}

.prereq-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

/* ---------- 未達・違反の横帯 ---------- */
.issue-banner {
  margin-bottom: 12px;
  padding: 12px 16px;
  border: 1px solid var(--el-color-danger);
  border-left-width: 5px;
  border-radius: var(--sp-radius);
  background: color-mix(in srgb, var(--el-color-danger) 8%, var(--sp-panel));

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: var(--el-color-danger);
  }
}

.issue-list {
  max-height: 240px;
  margin: 8px 0 0;
  padding: 0;
  overflow: auto;
  list-style: none;
}

.issue-item {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
  padding: 4px 0;
  font-size: 13px;
  line-height: 1.6;

  & + & {
    border-top: 1px dashed color-mix(in srgb, var(--el-color-danger) 25%, transparent);
  }

  &__tag {
    flex: none;
    padding: 0 6px;
    border-radius: 3px;
    font-size: 12px;
    color: #fff;
    background: var(--el-color-danger);

    &.is-hard_rule {
      background: var(--el-color-warning);
    }
  }

  &__title {
    font-weight: 700;
  }

  &__detail {
    color: var(--sp-text-2);
  }

  &__gap {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--el-color-danger);
  }
}

/* ---------- 表頭情報 ---------- */
.head-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 24px;
  padding: 12px 18px;
}

.meta-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 28px;
  margin: 0;
}

.meta-item {
  dt {
    font-size: 12px;
    color: var(--sp-muted);
  }

  dd {
    margin: 2px 0 0;
    font-size: 14px;
    font-weight: 600;
  }
}

.download-box {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.download-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.download-note {
  font-size: 12px;
  color: var(--sp-muted);
}

/* ---------- 時刻表 ---------- */
.day-grid-scroll {
  overflow-x: auto;
}

.day-grid {
  display: grid;
  grid-template-columns: repeat(var(--day-count, 7), minmax(150px, 1fr));
  min-width: calc(var(--day-count, 7) * 150px);
}

.day-col {
  padding: 0 6px 8px;
  border-right: 1px solid var(--sp-border);

  &:last-child {
    border-right: none;
  }

  &__head {
    position: sticky;
    top: 0;
    margin: 0 -6px 6px;
    padding: 6px 8px;
    border-bottom: 1px solid var(--sp-border);
    font-size: 13px;
    font-weight: 700;
    text-align: center;
    background: var(--sp-panel);

    &.is-sat {
      color: var(--el-color-primary);
    }

    &.is-sun {
      color: var(--el-color-danger);
    }
  }

  &__empty {
    padding: 8px 0;
    text-align: center;
    color: var(--sp-muted);
  }
}

.show-card {
  margin-bottom: 6px;
  padding: 6px 8px;
  border: 1px solid var(--sp-border);
  border-left: 3px solid var(--sp-border);
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;

  &.is-flagged {
    border-color: var(--el-color-danger);
    background: color-mix(in srgb, var(--el-color-danger) 10%, var(--sp-panel));
  }

  &__time {
    font-size: 13px;
    font-weight: 700;
  }

  &__title {
    font-weight: 600;
    word-break: break-all;
  }

  &__meta {
    color: var(--sp-text-2);
  }

  &__source {
    margin-left: 4px;
    padding: 0 4px;
    border: 1px solid var(--sp-border);
    border-radius: 3px;
  }
}

@media (max-width: 768px) {
  .export-page {
    padding: 0 12px 32px;
  }

  .download-box {
    align-items: flex-start;
  }
}
</style>
